import { all, not, Button, Div, Span, reactive, Br } from 'alkali'
import { Grid } from 'components/grid/Grid'
import Icon from 'components/Icon'
import * as arrowRight from 'icons/arrow-right-1.svg'
import * as arrowDown from 'icons/arrow-down-1.svg'
import * as networkIcon from 'icons/hierarchy-1.svg'
import * as libraryIcon from 'icons/read-search.svg'
import * as add from 'icons/add.svg'
import * as subtract from 'icons/subtract.svg'
import * as positiveDirectionalityIcon from 'icons/monitor-heart-rate-up.svg'
import * as negativeDirectionalityIcon from 'icons/monitor-heart-rate-down.svg'
import * as sortIcon from 'icons/data-transfer-vertical.svg'
import SelectivityDropdown from 'components/generic/SelectivityDropdown'
import { shortN } from 'components/studies/Study'
import { default as Result } from 'components/analysis/Result'
import { view as analysisView } from 'model/Analysis'
import './ResultsGrid.scss'
import { allOutcomes, selectedOutcomes, selectedTreatmentIds } from 'model/EvidenceSearch'
import { sorting, analysisType, updateAnalysis, analyzeTreatmentMap, showLiveAnalysis, isAnalysisOutcomePositive, docSearchQuery, outcomeSettings, structuredQuery, faqOrReadOnly } from 'model/SearchQuery'
import { outcomeMeasureName } from 'model/AnalyzeStudy'
import { getArticleCountForIntervention } from 'model/StudyCollection'
import { SortingDropdown } from '../studies/SortingDropdown'
import { DirectionalityDropdown } from '../studies/DirectionalityDropdown'
import { interventionDropdown } from '../filter/InterventionsGrid'
import { GroupsNetworkDialog } from './GroupsNetwork'
import { groupConnectivity } from 'model/GroupConnectivity'
import { getUnitType } from 'analysis/RowTypeUnit'
import { AutoSelectDialog } from '../studies/AutoSelectDialog'


const sortingDropdown = new SortingDropdown({
	sorts: [{
		priorityDescription: 'Analyzable',
		priorityTitle: 'Slide to the right to maximize retrieval of studies with relevant extracted data.',
		value: sorting.analyzable,
	}, {
		priorityDescription: 'RCT',
		priorityTitle: 'Slide to the right to maximize retrieval of studies by design, preferring randomized controlled trial.',
		value: sorting.design,
	}]
})

let currentOutcomes = reactive([{}])
const directionalityDropdown = new DirectionalityDropdown({
	outcomes: currentOutcomes,
})

const autoSelectDialog = new AutoSelectDialog()
const baseColumns = {
	studyCount: {
		label: '',
		flex: '0 0 1.4rem',
		sortable: false,
		width: '1.4rem',
		renderHeaderCell(th) {
			th.append(Icon({
				icon: libraryIcon,
				title: 'Studies that are included in this arm/group',
			}))
		}
	},
	autoSelectDialog: {
		label: '',
		flex: '0 0 1rem',
		width: '1rem',
		sortable: false,
		renderHeaderCell(th) {
			th.append(Span('.auto-select-control', ['☰']))
			th.onclick = (event) => {
				autoSelectDialog.show(event) 
			}
		}
	},
	expand: {
		label: '',
		flex: '0 0 1.5rem',
		width: '1.5rem',
		sortable: false,
		renderHeaderCell(th) {
			th.append(
				Span('.twisty.dgrid-expando-icon', {
					classes: {
						'expander-icon-s': showLiveAnalysis,
						'expander-icon-e': not(showLiveAnalysis),
					},
					content: '➤'
				}),
			)
			th.onclick = () => {
				showLiveAnalysis.put(!showLiveAnalysis.valueOf())
			}
		},
		renderCell(data, value, cell) {
			let expanded = reactive(false)
			return new Icon({
				expanded,
				icon: expanded.to(expanded => (expanded ? arrowDown : arrowRight)),
				onclick: () => {
					this.grid.expandRow(this.grid.row(cell))
				},
			})
		},
	},
	name: {
		label: 'Evidence Summary',
		sortable: false,
		renderHeaderCell(th) {
			th.textContent = 'Evidence Summary'
			th.title = 'Estimated outcomes of selected treatments, using computed relative effects applied to estimated baseline outcome'
		}
	},
	N: {
		label: '',
		flex: '0 0 5.5rem',
		width: '5.5rem',
		sortable: false,
		renderCell: (data, value) => {
			if (!value) {
				// grab the N from the first outcome in AI mode
				for (let key in data) {
					if (data[key] && data[key].N > 0) value = data[key].N
				}
			}
			return new Span('.study-n', [shortN(value) || '?'])
		},
	},
	groups: {
		label: 'Groups',
		flex: '0.4 0 7rem',
		width: '7rem',
		sortable: false,
		renderHeaderCell(th) {
			th.append(Span('.study-groups-label', {
				classes: {
					'incomplete-group-network': analysisType.to(analysisType =>
						(analysisType == 'bayesian' || analysisType == 'frequentist' || analysisType == 'auto') &&
						groupConnectivity.property('incomplete')),
				},
				content: [ Button('.icon-button', [ Icon({ icon: networkIcon }), ' Groups' ])],
				title: 'Select to choose how interventions are grouped',
				onclick(event) {
					new GroupsNetworkDialog({
					}).show(event)
				}
			}))
		}
	}
}
export class ResultsGrid extends Grid.with('.results-grid', {
	selectionMode: 'none',
	id: 'evidence-results-grid',
	loadingMessage: 'Analyzing...',
	classes: {
		expanded: showLiveAnalysis
	}
}) {
	outcomes: any
	created(properties) {
		properties.columns = selectedOutcomes.to(outcomes => {
			let columns = Object.assign({}, baseColumns)
			let label = structuredQuery.outcomes.valueOf()[0]?.label || outcomes[0]?.label
			for (let outcome of outcomes) {
				let unit = outcome.unit
				let unitType = outcomeSettings.unit.to(unit =>
					(unit && label == outcomeSettings.settingsFor) ?
					unit : getUnitType(outcome),
					newUnit => {
						outcomeSettings.settingsFor.put(label)
						outcomeSettings.unit.put(newUnit.replace(/-top$/, ''))
					})
				columns['outcome-' + outcome.id] = {
					flex: '1 1 50%',
					width: '50%',
					label,
					sortable: false,
					renderHeaderCell(th) {
						th.className += ' outcome-column available-column'
						let directionalityIcon = Button('.icon-button', {
							content: Icon('.outcome-directionality', {
								icon: outcome.isPositive ? positiveDirectionalityIcon : negativeDirectionalityIcon,
							}),
							title: 'Analyzing with the assumption that ' + (outcome.isPositive ? 'increasing ' : 'decreasing ') + label + ' indicates a better response. Click to update if needed.',
							onclick: (event) => {
								if (faqOrReadOnly.valueOf())
									return
								currentOutcomes.put([outcome])
								directionalityDropdown.analysisDirectionality = isAnalysisOutcomePositive.property(outcome.id)
								directionalityDropdown.show(event)
								event.stopPropagation()
							}
						})
						th.append([
							directionalityIcon,
							Span(label),
							SelectivityDropdown({
								value: unitType,
								disabled: structuredQuery.outcomes.to(outcomes => !(outcomes?.length > 0)),
								items: unitsToTypeCategories(outcome.units),
								showSearchInputInDropdown: false,
								title: all([structuredQuery.outcomes, unitType], (outcomes, unitType) =>
									outcomes?.length > 0 ?
									'Current unit/type is ' + unitType + ', click to select a different type/unit for the outcome measurement' :
									'Please select an outcome before selecting unit/type'),
								onclick(event) {
									event.stopPropagation()
								},
								readOnly: faqOrReadOnly.valueOf(),
							}),
							Button('.icon-button.analyzable-studies-slider', {
								display: docSearchQuery.to(query => !query?.query),
								title: 'Click to select priority of analyzable studies',
								onclick(event) {
									if (faqOrReadOnly.valueOf())
										return
									sortingDropdown.show(event, event.target)
								}
							}, [ Icon({ class: 'sort-icon', icon: sortIcon}) ]),
							Span('.legend-unit', [getUnitType(outcome)])
						])
					},
				}
				columns['value-' + outcome.id] = {
					flex: '0 0 12rem',
					width: '12rem',
					sortable: false,
					renderHeaderCell(th) {
						th.className += ' field-value'
						th.append(new Span([ outcomeMeasureName(outcome, true) ]))
					}
				}
			}
			return columns
		})
	}
	
	renderRow(result, options) {
		let resultRow = new Result({ result })
		if (this.shouldExpand(result)) {
			setTimeout(() => this.expandRow(this.row(resultRow)), 10)
		}
		if (this.activeTab == 'report') {
			setTimeout(() => resultRow.switchToForestPlot(), 10)	
		}
		return resultRow
	}
	switchToForestPlot() {
		let updated = Promise.resolve()
		//if (!showLiveAnalysis.valueOf())
			updated = updateAnalysis(true, analysisType != 'direct')
		updated.then(() => this.collection).then(() => {
			setTimeout(() => {
				if (this.activeTab == 'report') {
					for (let row of this.contentNode.querySelectorAll('section')) {
						row.switchToForestPlot()
					}
				}
			}, 200)
		})
	}
	switchToSources() {
		for (let row of this.contentNode.querySelectorAll('section')) {
			row.switchToSourceView()
		}
	}
	expandRow(row) {
		let element = row.element
		let icon = element.getElementsByTagName('streamline-icon')[0]
		let expanded = icon.expanded
		let expand = !expanded.valueOf()
		expanded.put(expand)
		element.classList[expand ? 'add' : 'remove']('expanded-row')
		let data = row.data
		let tr = row.element.firstChild.firstChild
		let cellIndex = 4
		for (let outcome of selectedOutcomes) {
			let outcomeCell = tr.childNodes[cellIndex++]
			let nameValues = outcomeCell.append(Div('.outcome-name-values'))
			for (let outcomeId in data) {
				if (outcomeId.startsWith(outcome.id)) {
					let effect = data[outcomeId]
					let outcome = allOutcomes.valueOf().find(outcome => outcome.id == outcomeId)
					let outcomeName = outcome.qualifiers.concat(outcome.root).join(' ')
					nameValues.append(
						Div('.outcome-name-value', [
							Div('.outcome-name-offset', [
								Div('.outcome-name', {
									content: outcomeName,
									title: outcomeName,
								}),
							]),
							renderBar(effect, outcome),
						])
					)
				}
			}
		}
		if (expand) {
			if (!element.expandedHeight) {
				element.style.height = 'auto'
				element.expandedHeight = element.offsetHeight
			}
			element.style.height = ''
			setTimeout(() => {
				element.style.height = element.expandedHeight + 'px'
			}, 40)
		} else {
			element.style.height = ''
		}
	}
	shouldExpand(data) {
		return false
		if (selectedTreatmentIds.valueOf().length == 1)
			return data.name == selectedTreatmentIds.valueOf()[0]
	}
}

function unitsToTypeCategories(unitTypes) {
	return unitTypes ? unitTypes.map(({ id, name, studyCount, children }) => {
		let menuText = name == '%' ? 'Binary %' :
			children ?
				name == 'Continuous' ? 'Average' : name :
				name.indexOf('(') > -1 ? name.slice(name.indexOf('(') + 1, -1) : 'units'
		return {
			id: (children && id[0] != '%') ? id + '-top' : id,
			text: id.replace(/ \(units\)/,''),
			menuText,
			additionalText: ' (' + studyCount + ')',
			title: studyCount + ' publications report outcome as ' + name,
			children: children && unitsToTypeCategories(children)
		}
	}) : []
}


function renderBar(effect, outcome) {
	if (!effect) return
	let variance = Math.random() * 40 + 20
	let rgb = outcome.isPositive ? '96,193,154' : '233,155,100'
	let outcomeBar = new Div(
		'.outcome-bar' + (outcome.isPositive ? '.positive-effect' : ''),
		{
			width: 0,
			background: `linear-gradient(90deg, rgba(${rgb},1) 0%, rgba(${rgb},1) ${100 -
				variance}%, rgba(${rgb},0) 100%)`,
		},
		[numToString(effect.effect) + (outcome.unit || '%')]
	)
	setTimeout(() => {
		outcomeBar.style.width = (effect.effect + 3) * 10 + 'px'
	}, 400)
	return outcomeBar
}

const roundingNumber = x =>
	Math.pow(10, Math.floor(Math.log10(Math.max(Math.abs(x), 0.0001)) - 2))
const numToString = x =>
	x
		.toFixed(Math.min(Math.max(-Math.floor(Math.log10(Math.abs(x))) + 2, 0), 8))
		.replace(/\.(\d*?)0+$/, (t, mantissa) => (mantissa ? '.' + mantissa : ''))
